import { useEffect } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

/**
 * How long to wait between asks for a Buddy link that isn't there yet (ms, ~50 s in all).
 * The API issues it in the background after S7 (a fellow passenger's once they have signed)
 * by asking WordPress, which usually takes a second or two; after the last ask the screen
 * stays without a card until the next visit.
 */
export const BUDDY_LINK_ASKS = [1500, 2500, 4000, 6000, 9000, 12000, 15000] as const;

/**
 * While `waiting`, refetch the query behind the Buddy card (the enquiry on S8, the claim view on
 * S12, the part on S15) on BUDDY_LINK_ASKS until the link arrives. Contract 1.2: `buddyLink` is
 * null until issued.
 */
export function useAwaitBuddyLink(waiting: boolean, queryKey: QueryKey) {
  const qc = useQueryClient();
  const key = JSON.stringify(queryKey);
  useEffect(() => {
    if (!waiting) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let i = 0;
    const next = () => {
      if (i >= BUDDY_LINK_ASKS.length) return;
      timer = setTimeout(() => {
        i += 1;
        void qc.invalidateQueries({ queryKey: JSON.parse(key) as QueryKey, exact: true }).finally(next);
      }, BUDDY_LINK_ASKS[i]);
    };
    next();
    return () => clearTimeout(timer);
  }, [waiting, key, qc]);
}
